'use client'

import React, { useState, useEffect, useRef, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { useAdmin } from '@/hooks/useAdmin'
import { useConversationOptimized as useConversation } from '@/hooks/useConversationOptimized'
import { useMemoryPreloader, setGlobalMemoryPreloader } from '@/hooks/useMemoryPreloader'
import { useOpenAIRealtime } from '@/hooks/useOpenAIRealtime'
import FollowUpButtons from '@/components/conversation/FollowUpButtons'
import { ProductResults } from '@/components/conversation/ProductResults'
import { detectInputNeedWithContext } from '@/utils/ai/inputDetection'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { FollowUpOption } from '@/utils/ai/types'
import { Mic, Send, X, Copy, MessageCircle, Paperclip, VolumeX, LayoutGrid } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { uploadFileToStorage, saveAttachmentToDatabase, addToUserFileLibrary, getSmartCategoryFromContext } from '@/utils/fileUpload'
import QuizModal from '@/components/conversation/QuizModal'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { VoiceSettingsModal } from '@/components/conversation/VoiceSettingsModal'
import { AttachmentsModal } from '@/components/conversation/AttachmentsModal'
import LanguagePicker from '@/components/ui/LanguagePicker'

interface StandardChatContainerProps {
    id: string
    showSidebar: boolean
    setShowSidebar: React.Dispatch<React.SetStateAction<boolean>>
}

export default function StandardChatContainer({ id, showSidebar, setShowSidebar }: StandardChatContainerProps) {
    const navigate = useNavigate()
    const { user, userProfile, isLoading: authLoading } = useAuth()
    const { isAdmin } = useAdmin()
    const supabase = createClientComponentClient()
    const [newMessage, setNewMessage] = useState('')
    const [showSmartInput, setShowSmartInput] = useState(false)
    const [smartInputPlaceholder, setSmartInputPlaceholder] = useState('')
    const [selectedImages, setSelectedImages] = useState<string[]>([])
    const [uploadedFiles, setUploadedFiles] = useState<Array<{ url: string, path: string, name: string, type: string, size: number }>>([])
    const [realtimeVoice, setRealtimeVoice] = useState<string>('alloy')
    const [showVoiceControl, setShowVoiceControl] = useState(false)
    const [showAttachments, setShowAttachments] = useState(false)
    const [showCategoryDrawer, setShowCategoryDrawer] = useState(false)
    const [showHiddenCategories, setShowHiddenCategories] = useState(false)
    const [hiddenCategoryIds, setHiddenCategoryIds] = useState<string[]>([])
    const [fontSize, setFontSize] = useState<'normal' | 'large' | 'xlarge'>('normal')
    const [chatCategories, setChatCategories] = useState<Array<{ id: string, name: string, icon: string }>>([])
    const categoryConstraintsRef = useRef<HTMLDivElement>(null)
    const dragContentRef = useRef<HTMLDivElement>(null)
    const [dragConstraints, setDragConstraints] = useState({ left: 0, right: 0 })
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const [loadGuardExpired, setLoadGuardExpired] = useState(false)
    const hasScrolledToBottomRef = useRef(false)
    const [voiceFollowUpOptions, setVoiceFollowUpOptions] = useState<FollowUpOption[]>([])

    const memoryPreloader = useMemoryPreloader()
    useEffect(() => {
        if (memoryPreloader) setGlobalMemoryPreloader(memoryPreloader)
    }, [memoryPreloader])

    const {
        conversation,
        messages,
        followUpOptions,
        productResults,
        isLoading,
        isStreaming,
        sendMessage,
        refreshMessages,
        quiz,
        isQuizOpen,
        setIsQuizOpen,
        submitQuizResults,
    } = useConversation(id)

    const {
        isConnected: isVoiceConnected,
        isListening: isVoiceListening,
        isSpeaking: isVoiceSpeaking,
        isMuted,
        toggleMute,
        currentTranscript,
        error: voiceError,
        stopVoiceSession,
        toggleVoiceSession,
        clearError: clearVoiceError
    } = useOpenAIRealtime(id, realtimeVoice)

    const fontSizeValues = { normal: '17px', large: '20px', xlarge: '24px' }
    const chatFontSize = fontSizeValues[fontSize]
    const [showVoiceSettings, setShowVoiceSettings] = useState(false)

    useEffect(() => {
        if (user?.id) {
            try {
                const raw = localStorage.getItem(`hidden_categories_${user.id}`)
                if (raw) setHiddenCategoryIds(JSON.parse(raw))
            } catch (e) {
                console.warn('Failed to parse hidden categories', e)
            }
        }
    }, [user?.id])

    const persistHidden = useCallback((next: string[]) => {
        setHiddenCategoryIds(next)
        if (user?.id) {
            localStorage.setItem(`hidden_categories_${user.id}`, JSON.stringify(next))
        }
    }, [user?.id])

    const hideCategory = useCallback((catId: string) => {
        if (!hiddenCategoryIds.includes(catId)) {
            persistHidden([...hiddenCategoryIds, catId])
        }
    }, [hiddenCategoryIds, persistHidden])

    const unhideCategory = useCallback((catId: string) => {
        persistHidden(hiddenCategoryIds.filter(id => id !== catId))
    }, [hiddenCategoryIds, persistHidden])

    const visibleCategories = chatCategories.filter(c => !hiddenCategoryIds.includes(c.id))
    const hiddenCategories = chatCategories.filter(c => hiddenCategoryIds.includes(c.id))

    const handleRefreshAndScroll = useCallback(async () => {
        await refreshMessages()
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
    }, [refreshMessages])

    const handleSwitchToTextMode = useCallback(async () => {
        stopVoiceSession()
        await new Promise(resolve => setTimeout(resolve, 500))
        await handleRefreshAndScroll()
    }, [stopVoiceSession, handleRefreshAndScroll])

    useEffect(() => {
        if (user?.user_metadata?.preferred_voice) setRealtimeVoice(user.user_metadata.preferred_voice)
    }, [user])

    const handleRealtimeVoiceChange = useCallback(async (voice: string) => {
        setRealtimeVoice(voice)
        if (user) {
            try { await supabase.auth.updateUser({ data: { preferred_voice: voice } }) } catch { }
        }
    }, [user, supabase])

    useEffect(() => {
        if (isVoiceConnected) {
            try { if (typeof window !== 'undefined') window.speechSynthesis.cancel() } catch { }
        }
    }, [isVoiceConnected])

    useEffect(() => {
        setShowVoiceControl(isVoiceListening || isVoiceSpeaking)
    }, [isVoiceListening, isVoiceSpeaking])

    useEffect(() => {
        if (!id) return
        const handler = (event: CustomEvent) => {
            if (event.detail.conversationId === id) {
                setTimeout(() => handleRefreshAndScroll(), 100)
            }
        }
        window.addEventListener('voiceMessageSaved', handler as EventListener)
        return () => window.removeEventListener('voiceMessageSaved', handler as EventListener)
    }, [id, handleRefreshAndScroll])

    useEffect(() => {
        const handler = (e: Event) => {
            const { conversationId: eventConvId, options } = (e as CustomEvent).detail
            if (eventConvId === id) setVoiceFollowUpOptions(options)
        }
        window.addEventListener('voiceOptionsReceived', handler as EventListener)
        return () => window.removeEventListener('voiceOptionsReceived', handler as EventListener)
    }, [id])

    useEffect(() => {
        if (!isVoiceConnected) setVoiceFollowUpOptions([])
    }, [isVoiceConnected])

    useEffect(() => {
        if (messages.length > 0 && !isLoading && isStreaming) {
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50)
        }
    }, [messages, isStreaming, isLoading])

    useEffect(() => {
        if (messages.length > 0 && !isLoading && !isStreaming) {
            const last = messages[messages.length - 1]
            if (last.role === 'assistant') {
                setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
            }
        }
    }, [isStreaming, messages, isLoading])

    useEffect(() => {
        if (messages.length > 0 && !hasScrolledToBottomRef.current && !isLoading) {
            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
                hasScrolledToBottomRef.current = true
            }, 100)
        }
    }, [messages.length, isLoading])

    useEffect(() => {
        if (messages.length > 0 && !isLoading) {
            const last = messages[messages.length - 1]
            if (last.role === 'assistant') {
                const detection = detectInputNeedWithContext(
                    last.content, followUpOptions, messages,
                    conversation?.category?.name || 'general'
                )
                setShowSmartInput(detection.shouldShowInput)
                setSmartInputPlaceholder(detection.placeholder)
            } else {
                setShowSmartInput(false)
            }
        }
    }, [messages, followUpOptions, isLoading, conversation?.category?.name])

    const adjustTextareaHeight = () => {
        const ta = textareaRef.current
        if (ta) {
            ta.style.height = 'auto'
            ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`
        }
    }

    useEffect(() => {
        let query = supabase.from('categories').select('id, name, icon').order('name')
        if (!isAdmin) query = query.eq('is_visible', true)
        query.then(({ data }: { data: any }) => { if (data) setChatCategories(data) })
    }, [supabase, isAdmin])

    useEffect(() => {
        const updateConstraints = () => {
            if (categoryConstraintsRef.current && dragContentRef.current) {
                const containerWidth = categoryConstraintsRef.current.offsetWidth
                const contentWidth = dragContentRef.current.scrollWidth
                setDragConstraints({ left: Math.min(0, containerWidth - contentWidth - 48), right: 0 })
            }
        }
        updateConstraints()
        const timer = setTimeout(updateConstraints, 500)
        window.addEventListener('resize', updateConstraints)
        return () => {
            window.removeEventListener('resize', updateConstraints)
            clearTimeout(timer)
        }
    }, [chatCategories])

    useEffect(() => {
        let tid: number | undefined
        const loading = authLoading || (isLoading && messages.length === 0 && !conversation)
        if (loading) tid = window.setTimeout(() => setLoadGuardExpired(true), 3000)
        else { setLoadGuardExpired(false); if (tid) window.clearTimeout(tid) }
        return () => { if (tid) window.clearTimeout(tid) }
    }, [authLoading, isLoading, messages.length, conversation])

    const handleFollowUpSelect = async (option: FollowUpOption, customInput?: string) => {
        let displayContent = customInput || option.text
        const rawPayload = customInput || (option as any).payload || option.text
        if (!customInput && /^\d+[.)]\s+/.test(displayContent)) {
            displayContent = displayContent.replace(/^\d+[.)]\s+/, '').trim()
        }
        await sendMessage(rawPayload, true, [], undefined, displayContent)
    }

    const handleFileSelect = async (files: FileList) => {
        const uploads: Array<{ url: string, path: string, name: string, type: string, size: number }> = []
        for (const file of Array.from(files).slice(0, 4)) {
            if (file.size > 10 * 1024 * 1024) { alert(`${file.name} is too large (max 10MB)`); continue }
            if (!user || !conversation) continue
            try {
                const f = await uploadFileToStorage(file, user.id, conversation.id,
                    typeof conversation.category === 'string' ? conversation.category : conversation.category?.name)
                uploads.push({ url: f.url, path: f.path, name: f.name, type: f.type, size: f.size })
            } catch { alert(`Failed to upload ${file.name}`) }
        }
        if (uploads.length) {
            setSelectedImages(p => [...p, ...uploads.map(f => f.url)].slice(0, 4))
            setUploadedFiles(p => [...p, ...uploads].slice(0, 4))
        }
    }

    const handleCameraCapture = async (files: FileList) => {
        const file = files[0]; if (!file || !user || !conversation) return
        if (file.size > 10 * 1024 * 1024) { alert('Photo too large (max 10MB)'); return }
        try {
            const f = await uploadFileToStorage(file, user.id, conversation.id,
                typeof conversation.category === 'string' ? conversation.category : conversation.category?.name)
            setSelectedImages(p => [...p, f.url].slice(0, 4))
            setUploadedFiles(p => [...p, { url: f.url, path: f.path, name: f.name, type: f.type, size: f.size }].slice(0, 4))
        } catch { alert('Failed to upload photo') }
    }

    const handleRemoveImage = (i: number) => setSelectedImages(p => p.filter((_, idx) => idx !== i))
    const handleRemoveFile = (i: number) => setUploadedFiles(p => p.filter((_, idx) => idx !== i))

    const handleStartOver = async () => {
        await sendMessage("I'd like to start over", true, selectedImages)
        setShowSmartInput(false); setSelectedImages([]); setUploadedFiles([])
    }

    const handleStartQuiz = async () => {
        await sendMessage('Start a quiz for me', true, [], { action: 'start_quiz' })
    }

    const handleCustomMessage = async () => {
        if (!newMessage.trim() && selectedImages.length === 0) return
        const message = newMessage.trim()
        setNewMessage('')
        await sendMessage(message, true, selectedImages)
        if (uploadedFiles.length > 0 && user && conversation) {
            try {
                const last = messages[messages.length - 1]
                if (last?.role === 'user') {
                    for (const f of uploadedFiles) {
                        await saveAttachmentToDatabase(last.id, f, user.id, conversation.id)
                        const cat = typeof conversation.category === 'string' ? conversation.category : conversation.category?.name
                        await addToUserFileLibrary(f, user.id,
                            getSmartCategoryFromContext(f.type, cat, message),
                            `Attachment from ${cat || 'general'} conversation`)
                    }
                }
            } catch { }
        }
        setSelectedImages([]); setUploadedFiles([])
        if (textareaRef.current) textareaRef.current.style.height = 'auto'
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleCustomMessage() }
    }

    const initialLoading = authLoading || (isLoading && messages.length === 0 && !conversation)
    if (initialLoading) {
        return (
            <div className="flex-1 flex items-center justify-center h-[100dvh]">
                <div className="text-center space-y-4">
                    <LoadingSpinner />
                    {loadGuardExpired && (
                        <div className="space-y-3">
                            <p style={{ color: '#4a5568' }}>This is taking longer than usual.</p>
                            <div className="flex items-center justify-center gap-3">
                                <Button onClick={() => navigate('/conversations')} style={{ background: '#4a7c5e', color: 'white' }}>Back to Conversations</Button>
                                <Button onClick={() => window.location.reload()} variant="ghost">Reload</Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    if (!conversation) {
        return (
            <div className="flex-1 flex items-center justify-center h-[100dvh]">
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-4 text-white" style={{ color: '#1a2740' }}>Conversation not found</h2>
                    <p className="mb-6" style={{ color: '#4a5568' }}>This conversation may have been deleted.</p>
                    <Button onClick={() => navigate('/dashboard')} style={{ background: '#4a7c5e', color: 'white' }}>Back to Dashboard</Button>
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col flex-1 min-w-0 h-full overflow-hidden">
            {/* Header */}
            <header
                className="flex-shrink-0 z-30"
                style={{ background: 'white', borderBottom: '1px solid #f0ebe0', boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}
            >
                <div className="flex items-center gap-2 px-3 sm:px-6" style={{ height: '64px', minHeight: '64px' }}>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            onClick={() => setShowSidebar(!showSidebar)}
                            className="flex items-center justify-center rounded-xl transition-colors hover:bg-[#f0ebe0]"
                            style={{ width: '40px', height: '40px', border: 'none', background: 'none', cursor: 'pointer', color: '#4a5568', flexShrink: 0 }}
                            title="Toggle sidebar"
                        >
                            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                        <div className="flex items-center gap-2.5">
                            <div className="flex items-center justify-center flex-shrink-0" style={{ width: '42px', height: '42px' }}>
                                <img src="/icons/blue-sage-logo.png" alt="Sage" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </div>
                            <div className="hidden sm:block">
                                <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: '18px', color: '#1a2740', lineHeight: 1.1 }}>Sage</div>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#22c55e', animation: 'pulse 2s infinite' }} />
                                    <span style={{ fontSize: '12px', color: '#4a7c5e' }}>Ready to chat</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="ml-auto flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        <button className="flex sm:hidden items-center justify-center rounded-xl transition-colors hover:bg-[#f0ebe0]" onClick={() => setShowCategoryDrawer(true)} style={{ width: '40px', height: '40px', border: 'none', background: 'none', cursor: 'pointer', color: '#4a7c5e', flexShrink: 0 }} title="View categories">
                            <LayoutGrid size={20} />
                        </button>
                        <LanguagePicker />
                        <button onClick={handleStartQuiz} disabled={isLoading} className="flex items-center gap-1 flex-shrink-0 transition-all disabled:opacity-50 rounded-xl border" style={{ padding: '8px 10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', background: '#faf7f2', borderColor: '#f0ebe0', color: '#4a5568', fontFamily: "'Source Serif 4', serif" }} title="Start a quiz">
                            <span style={{ fontSize: '15px' }}>📝</span>
                            <span className="hidden sm:inline">Quiz</span>
                        </button>
                        <div className="flex items-center flex-shrink-0 rounded-xl border p-0.5 gap-0.5" style={{ background: '#faf7f2', borderColor: '#f0ebe0' }}>
                            {(['normal', 'large', 'xlarge'] as const).map((sz) => (
                                <button key={sz} onClick={() => setFontSize(sz)} style={{ width: '30px', height: '30px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: sz === 'normal' ? '12px' : sz === 'large' ? '15px' : '19px', fontWeight: 700, background: fontSize === sz ? '#4a7c5e' : 'transparent', color: fontSize === sz ? 'white' : '#4a5568', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0 }}>A</button>
                            ))}
                        </div>
                        <button onClick={handleStartOver} disabled={isLoading} className="flex items-center gap-1 flex-shrink-0 transition-all disabled:opacity-50 rounded-xl border" style={{ padding: '8px 10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', background: '#faf7f2', borderColor: '#f0ebe0', color: '#4a5568', fontFamily: "'Source Serif 4', serif" }} title="Start over">
                            ↺ <span className="hidden sm:inline">Restart</span>
                        </button>
                    </div>
                </div>

                {chatCategories.length > 0 && (
                    <div ref={categoryConstraintsRef} className="hidden sm:block" style={{ borderTop: '1px solid #f0ebe0', background: 'white', overflow: 'hidden', position: 'relative' }}>
                        <motion.div ref={dragContentRef} drag="x" dragConstraints={dragConstraints} dragElastic={0.05} dragMomentum={true} className="flex gap-2 px-6 py-2.5 cursor-grab active:cursor-grabbing" style={{ width: 'max-content' }}>
                            {visibleCategories.map(cat => (
                                <div key={cat.id} className="relative group flex-shrink-0">
                                    <button onClick={() => navigate(`/category/${cat.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`)} className="flex items-center gap-1.5 transition-all duration-200 rounded-full border-2" style={{ padding: '7px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', background: conversation.category?.id === cat.id ? '#4a7c5e' : '#faf7f2', color: conversation.category?.id === cat.id ? 'white' : '#4a5568', borderColor: conversation.category?.id === cat.id ? '#4a7c5e' : 'transparent', boxShadow: conversation.category?.id === cat.id ? '0 2px 8px rgba(74,124,94,0.25)' : 'none' }}>
                                        <span style={{ fontSize: '15px' }}>{cat.icon}</span>
                                        <span>{cat.name}</span>
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); hideCategory(cat.id) }} className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity border-2 border-white shadow-sm" style={{ cursor: 'pointer', fontSize: '10px' }}><X size={10} strokeWidth={3} /></button>
                                </div>
                            ))}
                            {hiddenCategories.length > 0 && (
                                <button onClick={() => setShowHiddenCategories(true)} className="flex-shrink-0 flex items-center gap-1.5 transition-all duration-200 rounded-full border-2 border-dashed border-[#b8d9c4] text-[#4a7c5e]" style={{ padding: '7px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', background: '#e8f2ec' }}>
                                    <LayoutGrid size={14} />
                                    <span>Manage Hidden ({hiddenCategories.length})</span>
                                </button>
                            )}
                        </motion.div>
                    </div>
                )}
            </header>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
                <div className="w-full max-w-4xl mx-auto px-3 sm:px-6 md:px-8 pt-4 sm:pt-6 pb-24">
                    {messages.map((message, index) => {
                        const msgDate = new Date(message.created_at)
                        const prevDate = index > 0 ? new Date(messages[index - 1].created_at) : null
                        const showDateSep = !prevDate || msgDate.toDateString() !== prevDate.toDateString()
                        const today = new Date()
                        const dateLabel = msgDate.toDateString() === today.toDateString()
                            ? `Today, ${msgDate.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}`
                            : msgDate.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

                        return (
                            <div key={message.id}>
                                {showDateSep && (
                                    <div className="flex justify-center py-4">
                                        <span style={{ fontSize: '12px', color: '#8a7e6b', fontWeight: 500, letterSpacing: '0.03em' }}>{dateLabel}</span>
                                    </div>
                                )}
                                <div className={`flex mb-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    {message.role === 'assistant' && (
                                        <div className="flex-shrink-0 flex items-center justify-center mt-1 mr-2.5" style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#2d5a3d' }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10" />
                                                <path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" />
                                            </svg>
                                        </div>
                                    )}
                                    <div className="relative" style={{ maxWidth: 'min(80%, 680px)', padding: '14px 18px', borderRadius: message.role === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px', background: message.role === 'user' ? '#4a7c5e' : 'white', color: message.role === 'user' ? 'white' : '#1a2740', border: message.role === 'assistant' ? '1px solid #f0ebe0' : 'none', boxShadow: message.role === 'user' ? '0 4px 14px rgba(74,124,94,0.25)' : '0 2px 16px rgba(0,0,0,0.06)' }}>
                                        {message.role === 'assistant' && (
                                            <div className="absolute top-2 right-2 flex gap-1">
                                                <button style={{ color: '#4a7c5e', cursor: 'pointer', background: 'none', border: 'none', padding: '2px' }} onClick={async (e) => { try { await navigator.clipboard.writeText(message.content); e.currentTarget.title = 'Copied!'; setTimeout(() => e.currentTarget.title = 'Copy', 1000) } catch { } }} title="Copy"><Copy size={14} /></button>
                                                <button style={{ color: '#4a7c5e', cursor: 'pointer', background: 'none', border: 'none', padding: '2px' }} onClick={() => { if (textareaRef.current) { textareaRef.current.value = `[Referencing: "${message.content.slice(0, 100)}..."]\n\n` + textareaRef.current.value; textareaRef.current.focus(); adjustTextareaHeight() } }} title="Reference in reply"><MessageCircle size={14} /></button>
                                            </div>
                                        )}
                                        <div className="prose max-w-none" style={{ fontSize: chatFontSize, lineHeight: 1.75, color: message.role === 'user' ? 'white' : '#1a2740', '--tw-prose-body': message.role === 'user' ? 'white' : '#1a2740' } as React.CSSProperties}>
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                                        </div>
                                        {message.role === 'assistant' && index === messages.length - 1 && productResults.length > 0 && (
                                            <ProductResults products={productResults} />
                                        )}
                                        <div style={{ fontSize: '11px', marginTop: '6px', color: message.role === 'user' ? 'rgba(255,255,255,0.55)' : '#4a7c5e', opacity: 0.8, textAlign: message.role === 'user' ? 'right' : 'left' }}>
                                            {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>
                                    {message.role === 'user' && (
                                        <div className="flex-shrink-0 flex items-center justify-center ml-2.5 font-bold" style={{ width: '34px', height: '34px', borderRadius: '10px', background: '#c9903a', color: 'white', fontSize: '13px', boxShadow: '0 2px 8px rgba(201,144,58,0.3)', alignSelf: 'flex-end' }}>
                                            {user?.email?.[0].toUpperCase() || 'U'}
                                        </div>
                                    )}
                                </div>
                                {/* Follow-up buttons moved below message list for better visibility */}
                            </div>
                        )
                    })}
                    {isLoading && (
                        <div className="flex justify-start mb-4">
                            <div className="flex-shrink-0 flex items-center justify-center mr-2.5" style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#2d5a3d' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" />
                                    <line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" />
                                </svg>
                            </div>
                            <div style={{ padding: '14px 18px', borderRadius: '20px 20px 20px 4px', background: 'white', border: '1px solid #f0ebe0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                <div className="flex items-center gap-2">
                                    <div className="flex gap-1">
                                        {[0, 0.15, 0.3].map((delay, i) => (
                                            <div key={i} className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#4a7c5e', animationDelay: `${delay}s` }} />
                                        ))}
                                    </div>
                                    <span style={{ fontSize: '13px', color: '#4a7c5e' }}>Sage is thinking…</span>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} style={{ height: 20 }} />
                </div>
            </div>

            {/* Quick Actions Area - Fixed above input */}
            {messages.length > 0 && !isLoading && !isStreaming && (
                <div className="flex-shrink-0 bg-white border-t border-[#f0ebe0] px-3 pt-2">
                    <div className="max-w-4xl mx-auto">
                        {/* Normal Follow-ups */}
                        {messages[messages.length - 1].role === 'assistant' && (messages[messages.length - 1].metadata as any)?.followUpOptions && voiceFollowUpOptions.length === 0 && (
                            <div className="mb-2">
                                <FollowUpButtons
                                    options={(messages[messages.length - 1].metadata as any).followUpOptions}
                                    onSelect={handleFollowUpSelect}
                                    category={conversation?.category?.name || 'general'}
                                    isLoading={isLoading}
                                />
                            </div>
                        )}

                        {/* Voice Follow-ups */}
                        {voiceFollowUpOptions.length > 0 && (
                            <div className="mb-2">
                                <FollowUpButtons options={voiceFollowUpOptions} onSelect={handleFollowUpSelect} isLoading={isLoading} allowCustomInput={false} category={conversation?.category?.name || 'general'} />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Input Area */}
            <div className="flex-shrink-0 z-20 bg-white border-t border-[#f0ebe0] p-3 pb-safe">
                <div className="max-w-4xl mx-auto">
                    {(currentTranscript || voiceError) && (
                        <div className="mb-2 p-3 rounded-xl border bg-[#f8f9fa]">
                            <div style={{ fontSize: '13px', color: '#1a2740' }}>
                                <span style={{ fontWeight: 600, color: '#4a7c5e' }}>🎤 {isVoiceListening ? 'Listening…' : isVoiceSpeaking ? 'Speaking…' : 'Voice active'}</span>
                                <div style={{ marginTop: '4px' }}>{currentTranscript}</div>
                            </div>
                            {voiceError && <div className="mt-2 text-[#ef4444] text-xs flex items-center justify-between"><span>{voiceError}</span><button onClick={clearVoiceError}><X size={14} /></button></div>}
                        </div>
                    )}
                    <div className="bg-[#faf7f2] border-2 border-[#f0ebe0] rounded-[18px] p-1.5 flex items-end gap-2 shadow-sm">
                        <button onClick={toggleVoiceSession} disabled={isLoading} className="w-11 h-11 flex-shrink-0 rounded-xl flex items-center justify-center transition-all" style={{ background: isVoiceListening ? '#ef4444' : '#e8f2ec', color: isVoiceListening ? 'white' : '#4a7c5e' }}>
                            <Mic size={20} className={isVoiceListening ? 'animate-pulse' : ''} />
                        </button>
                        <textarea
                            ref={textareaRef}
                            value={newMessage}
                            onChange={e => { setNewMessage(e.target.value); adjustTextareaHeight() }}
                            onKeyPress={handleKeyPress}
                            placeholder="Type your message here, or press the microphone to speak…"
                            disabled={isLoading}
                            rows={1}
                            style={{ flex: 1, minHeight: '28px', maxHeight: '120px', resize: 'none', border: 'none', outline: 'none', background: 'transparent', padding: '8px 0', fontSize: chatFontSize, color: '#1a2740', lineHeight: 1.5, fontFamily: "'Source Serif 4', Georgia, serif" }}
                        />
                        <button onClick={() => setShowAttachments(true)} className="p-2 text-gray-400" title="Attach files"><Paperclip size={20} /></button>
                        <button onClick={handleCustomMessage} disabled={!newMessage.trim() && selectedImages.length === 0 || isLoading} className="h-11 px-5 rounded-xl bg-[#4a7c5e] text-white font-bold flex items-center gap-2 shadow-md transition-all active:scale-95 disabled:opacity-50">
                            Send <Send size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <QuizModal isOpen={isQuizOpen} quiz={quiz} onClose={() => setIsQuizOpen(false)} onSubmit={submitQuizResults} />
            <VoiceSettingsModal isOpen={showVoiceSettings} onClose={() => setShowVoiceSettings(false)} realtimeVoice={realtimeVoice} onRealtimeVoiceChange={handleRealtimeVoiceChange} isMuted={isMuted} onToggleMute={toggleMute} isVoiceListening={isVoiceListening} onVoiceToggle={toggleVoiceSession} isVoiceSpeaking={isVoiceSpeaking} onStopSpeaking={stopVoiceSession} currentTranscript={currentTranscript} voiceError={voiceError} onClearVoiceError={clearVoiceError} />
            <AttachmentsModal isOpen={showAttachments} onClose={() => setShowAttachments(false)} onFileSelect={handleFileSelect} onCameraCapture={handleCameraCapture} selectedImages={selectedImages} onRemoveImage={handleRemoveImage} uploadedFiles={uploadedFiles} onRemoveFile={handleRemoveFile} />

            <AnimatePresence>
                {showCategoryDrawer && (
                    <div className="fixed inset-0 z-[100] sm:hidden">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowCategoryDrawer(false)} className="absolute inset-0 bg-black/40" />
                        <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} className="absolute bottom-0 left-0 right-0 bg-[#faf7f2] rounded-t-[32px] p-6 max-h-[85vh] overflow-y-auto">
                            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-6" />
                            {visibleCategories.map(cat => (
                                <button key={cat.id} onClick={() => { navigate(`/category/${cat.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`); setShowCategoryDrawer(false) }} className="w-full p-4 mb-2 bg-white rounded-2xl flex items-center gap-4 border border-[#f0ebe0]">
                                    <span className="text-2xl">{cat.icon}</span>
                                    <span className="font-bold">{cat.name}</span>
                                </button>
                            ))}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showHiddenCategories && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center px-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowHiddenCategories(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
                        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="relative bg-white rounded-[24px] shadow-2xl w-full max-w-md overflow-hidden">
                            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-[#1a2740] text-white">
                                <h3 className="text-xl font-bold flex items-center gap-2 text-white"><LayoutGrid size={20} /> Hidden Categories</h3>
                                <button onClick={() => setShowHiddenCategories(false)} className="p-1 hover:bg-white/10 rounded-lg"><X size={20} /></button>
                            </div>
                            <div className="p-6 max-h-[60vh] overflow-y-auto bg-[#faf7f2]">
                                {hiddenCategories.length === 0 ? <p className="text-center py-8 text-gray-500">No hidden categories!</p> : (
                                    <div className="grid gap-3">
                                        {hiddenCategories.map(cat => (
                                            <div key={cat.id} className="flex items-center gap-4 p-3 bg-white rounded-xl border border-[#f0ebe0] shadow-sm">
                                                <span className="text-2xl">{cat.icon}</span>
                                                <span className="flex-1 font-bold text-[#1a2740]">{cat.name}</span>
                                                <button onClick={() => unhideCategory(cat.id)} className="px-4 py-2 bg-[#4a7c5e] text-white rounded-full text-sm font-bold">Restore</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    )
}
